import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@shared/lib/cn';

export interface MultiSelectOption {
  value: string;
  label: string;
  /** Optional trailing count, shown muted. */
  hint?: string;
}

interface MultiSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: MultiSelectOption[];
  /** Shown when nothing is selected. */
  placeholder?: string;
  className?: string;
}

/**
 * A styled trigger plus a popover checkbox list.
 *
 * Selecting does not close the popover — picking several statuses is the whole
 * point, and a menu that closed on each click would make that tedious.
 */
export function MultiSelect({
  values,
  onChange,
  options,
  placeholder = 'All',
  className,
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const selected = new Set(values);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  const toggle = (value: string) => {
    const next = new Set(selected);

    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }

    // Preserve the options' order rather than click order, so the URL and the
    // trigger label stay stable however the selection was made.
    onChange(options.map((option) => option.value).filter((option) => next.has(option)));
  };

  const label =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? (options.find((option) => option.value === values[0])?.label ?? '1 selected')
        : `${values.length} selected`;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-lg border px-2.5 text-sm transition-colors',
          isOpen ? 'border-brand-500 bg-white' : 'border-slate-200 bg-white hover:bg-slate-50',
        )}
      >
        <span className={cn('truncate', values.length === 0 ? 'text-slate-500' : 'text-slate-900')}>
          {label}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {isOpen && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute left-0 top-full z-50 mt-1 min-w-full overflow-hidden rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
        >
          {values.length > 0 && (
            <div className="mb-1 border-b border-slate-100 pb-1">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full rounded-md px-2 py-1 text-left text-xs text-slate-500 transition-colors hover:bg-slate-50"
              >
                Clear selection
              </button>
            </div>
          )}

          {options.length === 0 ? (
            <p className="px-2 py-1.5 text-xs text-slate-400">No options</p>
          ) : (
            options.map((option) => {
              const isSelected = selected.has(option.value);

              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <span
                    aria-hidden
                    className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      isSelected
                        ? 'border-brand-600 bg-brand-600 text-white'
                        : 'border-slate-300 bg-white',
                    )}
                  >
                    {isSelected && <Check className="h-3 w-3" strokeWidth={3} />}
                  </span>

                  <span className="flex-1 truncate">{option.label}</span>

                  {option.hint && (
                    <span className="shrink-0 text-xs tabular-nums text-slate-400">
                      {option.hint}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
